import { useThemeMode } from './themeMode';
import type { ThemeMode } from './ptcThemes';

/**
 * Theme-aware status / semantic colors — the single home for the status hex
 * values that were previously inlined as light-only constants in
 * `types/index.ts` and `constants/index.ts`.
 *
 * Light keeps the app's established palette exactly (so light stays
 * pixel-faithful). Dark is a deliberate recalibration, not an inversion:
 * saturated slabs are softened to bright-on-deep so a chip does not glare on a
 * dark card, and colored text is lifted for >= 4.5:1 (WCAG AA) on the dark
 * surfaces.
 *
 * The maps are keyed by the same string literal unions the pages already use
 * (TaxReturnStatus, InquiryStatus, ...), so a consumer swaps
 * `statusColors[s]` for `useStatusColorMaps().status[s]` with no other change.
 */

type ColorMap = Record<string, string>;

interface StatusColorMaps {
  status: ColorMap;
  reviewStatus: ColorMap;
  appointmentType: ColorMap;
  filingType: ColorMap;
  drakeMatchStatus: ColorMap;
  inquiryStatus: ColorMap;
  inquiryPriority: ColorMap;
  optOutReason: ColorMap;
  sharedInvoiceStatus: ColorMap;
  preparerBillStatus: ColorMap;
}

const light: StatusColorMaps = {
  status: {
    scheduled: '#6B7280',
    pending_resolution: '#EF4444',
    documents_pending: '#F59E0B',
    ready_to_prepare: '#22C55E',
    in_preparation: '#3B82F6',
    in_review: '#8B5CF6',
    needs_corrections: '#F97316',
    ready_for_signature: '#EC4899',
    waiting_for_payment: '#D97706',
    ready_for_efile: '#14B8A6',
    e_filed: '#10B981',
    completed: '#059669',
    inactive: '#9CA3AF',
  },
  reviewStatus: {
    not_started: '#6B7280',
    in_progress: '#3B82F6',
    completed: '#10B981',
    completed_pending_payment: '#F59E0B',
    changes_required_preparer: '#F97316',
    changes_required_taxpayer: '#EF4444',
  },
  appointmentType: {
    'in-person': '#0078d4',
    virtual: '#8B5CF6',
    'drop-off': '#F97316',
  },
  filingType: {
    single: '#3B82F6',
    joint: '#8B5CF6',
    mfs: '#EC4899',
    hoh: '#F59E0B',
    business: '#10B981',
  },
  drakeMatchStatus: {
    pending_review: '#F59E0B',
    matched: '#10B981',
    multiple_matches: '#8B5CF6',
    no_match: '#EF4444',
  },
  inquiryStatus: {
    new: '#0078d4',
    in_progress: '#8B5CF6',
    waiting_for_client: '#F59E0B',
    resolved: '#10B981',
    closed: '#6B7280',
  },
  inquiryPriority: {
    low: '#6B7280',
    normal: '#3B82F6',
    high: '#F59E0B',
    urgent: '#EF4444',
  },
  optOutReason: {
    not_filing_this_year: '#F59E0B',
    using_different_preparer: '#EF4444',
    doing_own_taxes: '#3B82F6',
    deceased: '#6B7280',
    moved_away: '#8B5CF6',
    unsubscribe_request: '#EC4899',
    other: '#6B7280',
  },
  sharedInvoiceStatus: {
    draft: '#6B7280',
    sent: '#3B82F6',
    paid: '#10B981',
    partial: '#F59E0B',
    void: '#EF4444',
  },
  preparerBillStatus: {
    draft: '#6B7280',
    generated: '#3B82F6',
    paid: '#10B981',
  },
};

/**
 * Dark recalibration. These are brighter, slightly desaturated versions of the
 * light hues tuned to sit on the #14181e / #1d232b dark surfaces — a filled
 * chip (white glyph) stays legible and a colored-text/outline chip clears AA.
 * The keying is identical to `light`, so every status resolves.
 */
const dark: StatusColorMaps = {
  status: {
    scheduled: '#8b95a2',
    pending_resolution: '#ff6b6b',
    documents_pending: '#f5a83c',
    ready_to_prepare: '#4ec97a',
    in_preparation: '#5aa5f0',
    in_review: '#b9a5e3',
    needs_corrections: '#ff9f52',
    ready_for_signature: '#f072b0',
    waiting_for_payment: '#e0a020',
    ready_for_efile: '#3fc6b6',
    e_filed: '#3fce87',
    completed: '#2fb57a',
    inactive: '#7f8996',
  },
  reviewStatus: {
    not_started: '#8b95a2',
    in_progress: '#5aa5f0',
    completed: '#3fce87',
    completed_pending_payment: '#f5a83c',
    changes_required_preparer: '#ff9f52',
    changes_required_taxpayer: '#ff6b6b',
  },
  appointmentType: {
    'in-person': '#5eb2ec',
    virtual: '#b9a5e3',
    'drop-off': '#ff9f52',
  },
  filingType: {
    single: '#5aa5f0',
    joint: '#b9a5e3',
    mfs: '#f072b0',
    hoh: '#f5a83c',
    business: '#3fce87',
  },
  drakeMatchStatus: {
    pending_review: '#f5a83c',
    matched: '#3fce87',
    multiple_matches: '#b9a5e3',
    no_match: '#ff6b6b',
  },
  inquiryStatus: {
    new: '#5eb2ec',
    in_progress: '#b9a5e3',
    waiting_for_client: '#f5a83c',
    resolved: '#3fce87',
    closed: '#8b95a2',
  },
  inquiryPriority: {
    low: '#8b95a2',
    normal: '#5aa5f0',
    high: '#f5a83c',
    urgent: '#ff6b6b',
  },
  optOutReason: {
    not_filing_this_year: '#f5a83c',
    using_different_preparer: '#ff6b6b',
    doing_own_taxes: '#5aa5f0',
    deceased: '#8b95a2',
    moved_away: '#b9a5e3',
    unsubscribe_request: '#f072b0',
    other: '#8b95a2',
  },
  sharedInvoiceStatus: {
    draft: '#8b95a2',
    sent: '#5aa5f0',
    paid: '#3fce87',
    partial: '#f5a83c',
    void: '#ff6b6b',
  },
  preparerBillStatus: {
    draft: '#8b95a2',
    generated: '#5aa5f0',
    paid: '#3fce87',
  },
};

export const statusColorMaps: Record<ThemeMode, StatusColorMaps> = { light, dark };

/** The status/semantic palette for the active theme mode. */
export function useStatusColorMaps(): StatusColorMaps {
  const { mode } = useThemeMode();
  return statusColorMaps[mode];
}
